import * as React from 'react';
import {WithI18nProps, withI18n, I18n} from '@shopify/react-i18n';
import {
  ChoiceList,
  Heading,
  TextContainer,
  DisplayText,
  TextStyle,
  Button,
} from '@shopify/polaris';
import {
  ServiceListingCard,
  FiltersContainer,
  AppLayout,
  LoginModal,
} from 'sections/services-marketplace/experts/components';
import {
  ServiceListing,
  ServiceDimension,
  PublicServiceListing,
} from 'sections/types';
import {StringMap} from 'utilities/types';
import {
  fetchTaxonomy,
  Taxonomy,
  Routes,
  fetchServiceDimensions,
} from 'sections/services-marketplace/experts/utilities';
import {LoginModalContext} from 'sections/services-marketplace/experts/components/login-modal/LoginModal';

import * as styles from './ServiceListings.scss';

import i18nArgs from '../../locales';

interface OwnProps {
  serviceListings: PublicServiceListing[];
  currentFilter: string[];
  serviceDimensionsMap: StringMap<ServiceDimension>;
  expertHandle: string;
  onFilterChange(currentFilter: string[]): void;
}

type CombinedProps = OwnProps & WithI18nProps;

interface State {
  modalOpen: boolean;
}

class ServiceListings extends React.Component<CombinedProps, State> {
  state: State = {
    modalOpen: false,
  };

  taxonomy = fetchTaxonomy();

  toggleModal = () => {
    this.setState(({modalOpen}) => ({modalOpen: !modalOpen}));
  };

  renderServiceListings() {
    const {
      serviceListings,
      currentFilter,
      onFilterChange,
      serviceDimensionsMap,
      expertHandle,
      i18n,
    } = this.props;

    const choices = buildFilters({
      serviceListings: visibleListings(serviceListings),
      i18n,
      taxonomy: this.taxonomy,
    });

    const filteredListings = filterServiceListings({
      currentFilter,
      serviceListings: visibleListings(serviceListings),
      taxonomy: this.taxonomy,
    });

    return (
      <AppLayout.Container>
        <AppLayout.Section size="OneFifth">
          <FiltersContainer
            buttonText={i18n.translate('Profile.Overview.Filters.submit')}
          >
            <TextContainer spacing="tight">
              <Heading>
                {i18n.translate('Profile.Overview.Filters.heading')}
              </Heading>
              <ChoiceList
                choices={choices}
                selected={currentFilter}
                onChange={onFilterChange}
              />
            </TextContainer>
          </FiltersContainer>
        </AppLayout.Section>
        <AppLayout.Section fill>
          <div className={styles.Grid}>
            {filteredListings.map(serviceListing => {
              const serviceDimensions = fetchServiceDimensions(
                serviceDimensionsMap,
                serviceListing.handle
              );
              const {
                totalJobsCompleted,
                averageRating,
                totalReviews,
              } = serviceDimensions;

              return (
                <ServiceListingCard
                  serviceHandle={serviceListing.handle}
                  label={serviceListing.shortLabel}
                  startingPrice={serviceListing.startingPrice}
                  link={Routes.profileServiceListingPath(
                    expertHandle,
                    serviceListing.handle
                  )}
                  totalJobsCompleted={totalJobsCompleted}
                  averageRating={averageRating}
                  totalReviews={totalReviews}
                  key={serviceListing.handle}
                />
              );
            })}
          </div>
        </AppLayout.Section>
      </AppLayout.Container>
    );
  }

  renderEmptyState() {
    const {i18n} = this.props;
    const {modalOpen} = this.state;

    return (
      <AppLayout.Container>
        <TextContainer>
          <DisplayText size="large" element="h2">
            {i18n.translate('Profile.Overview.EmptyState.title')}
          </DisplayText>
          <DisplayText size="small" element="p">
            <TextStyle variation="subdued">
              {i18n.translate('Profile.Overview.EmptyState.text')}
            </TextStyle>
          </DisplayText>
          <Button primary onClick={this.toggleModal}>
            {i18n.translate('Profile.Overview.EmptyState.buttonText')}
          </Button>
        </TextContainer>
        <LoginModal
          context={LoginModalContext.CreateJob}
          open={modalOpen}
          onClose={this.toggleModal}
        />
      </AppLayout.Container>
    );
  }

  render() {
    const {serviceListings} = this.props;

    return (
      <div className={styles.ServiceListingsContainer}>
        {visibleListings(serviceListings).length
          ? this.renderServiceListings()
          : this.renderEmptyState()}
      </div>
    );
  }
}

function visibleListings(serviceListings: ServiceListing[]) {
  return serviceListings.filter(serviceListing => serviceListing.startingPrice);
}

type Filters = StringMap<{
  label: string;
  value: string;
}>;

function buildFilters({
  serviceListings,
  i18n,
  taxonomy,
}: {
  serviceListings: ServiceListing[];
  i18n: I18n;
  taxonomy: Taxonomy;
}) {
  const filters: Filters = serviceListings.reduce(
    (filters: Filters, service: ServiceListing) => {
      const ancestors = taxonomy.getAncestors(service.handle);

      if (!ancestors.length) {
        return filters;
      }

      const {handle, shortLabel} = ancestors[0];

      return {
        ...filters,
        [handle]: {
          label: shortLabel,
          value: handle,
        },
      };
    },
    {all: {label: i18n.translate('Profile.Overview.Filters.all'), value: ''}}
  );

  return Object.keys(filters).map(key => filters[key]);
}

function filterServiceListings({
  currentFilter,
  serviceListings,
  taxonomy,
}: {
  currentFilter: string[];
  serviceListings: ServiceListing[];
  taxonomy: Taxonomy;
}) {
  return currentFilter[0] === ''
    ? serviceListings
    : serviceListings.filter(service => {
        const ancestors = taxonomy.getAncestors(service.handle);
        return ancestors.length && ancestors[0].handle === currentFilter[0];
      });
}

export default withI18n(i18nArgs)(ServiceListings);
